import { type MouseEventHandler } from 'react';
import { type FilteredRestaurantsCarouselMode } from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import { cn } from '@discover-lib/utils';
import Button from '@discover-ui/button';
import FilteredRestaurantsCarousel, { MIN_CAROUSEL_RESTAURANTS } from './restaurantCarousel';
import FilteredRestaurantsCarouselSkeleton from './skeleton';
import useFilteredRestaurantsCarousel from './useFilteredRestaurantsCarousel';

const HEADINGS_BY_MODE: Record<FilteredRestaurantsCarouselMode, string> = {
  newArrivals: 'New arrivals',
  mostPopular: 'Most popular',
  availableToday: 'Available today',
};

export type ListRestaurantsCarouselProps = {
  regionId: number;
  mode?: FilteredRestaurantsCarouselMode;
  heading?: string;
  className?: string;
  id?: string;
  preserveSpace?: boolean;
  enabled?: boolean;
  onSeeAllClick?: MouseEventHandler<HTMLButtonElement>;
};

export default function ListRestaurantsCarousel({
  regionId,
  mode = 'newArrivals',
  heading,
  className,
  id,
  preserveSpace = false,
  enabled = true,
  ...carouselProps
}: ListRestaurantsCarouselProps) {
  const { restaurants, isLoading, hasError } = useFilteredRestaurantsCarousel({
    regionId,
    mode,
    enabled,
  });

  const resolvedHeading = heading ?? HEADINGS_BY_MODE[mode];

  const unavailable = !isLoading && (hasError || restaurants.length < MIN_CAROUSEL_RESTAURANTS);
  if (unavailable && !preserveSpace) return null;

  let content;
  if (unavailable) {
    content = (
      <>
        <div className="invisible" aria-hidden="true">
          <FilteredRestaurantsCarouselSkeleton heading={resolvedHeading} />
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-3">
          <h2 className="m-0 text-base font-semibold text-black">{resolvedHeading}</h2>
          <p className="m-0 text-center text-gray">
            {hasError
              ? 'New arrivals are temporarily unavailable.'
              : 'Explore the latest restaurants in this area.'}
          </p>
          <Button kind="outline" onClick={carouselProps.onSeeAllClick}>
            Browse restaurants
          </Button>
        </div>
      </>
    );
  } else if (isLoading) {
    content = <FilteredRestaurantsCarouselSkeleton heading={resolvedHeading} />;
  } else {
    content = (
      <FilteredRestaurantsCarousel
        {...carouselProps}
        restaurants={restaurants}
        heading={resolvedHeading}
      />
    );
  }

  return (
    <section
      id={id}
      aria-busy={isLoading}
      className={cn('relative w-full bg-gray-100/50 py-5 lg:bg-transparent', className)}>
      {content}
    </section>
  );
}
